import {ReportType} from '../../../types/report-type'
import {restoreLojasFilters, validateLojasFilters} from '../lojasRelacionamento'
import {FilterField, FilterOption, REPORT_FILTER_SCHEMAS} from './reportFilterSchema'

export const isRelationshipReport = (type: string) =>
  type === 'lojas_quase_churn' || type === 'lojas_quase_fidelizadas'
export function selectionValues(value: any): string[] {
  const items = Array.isArray(value)
    ? value
    : value === undefined || value === null || value === ''
    ? []
    : [value]
  return Array.from(new Set(items.map((item) => String(item?.value ?? item)).filter(Boolean)))
}
export function singleSelection(field: FilterField, value: any): string {
  const selected = selectionValues(value).find(
    (item) => !field.required || field.options?.some((option) => option.value === item)
  )
  return (
    selected ||
    (field.required ? selectionValues(field.initial)[0] || field.options?.[0]?.value || '' : '')
  )
}
export function localDate(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null
  const [year, month, day] = value.split('-').map(Number)
  const result = new Date(year, month - 1, day)
  return dateString(result) === value ? result : null
}
export function dateString(date: Date | null): string {
  return date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
        date.getDate()
      ).padStart(2, '0')}`
    : ''
}
export function restoreReportFilters(
  type: ReportType,
  data: any = {},
  clear = false
): Record<string, any> {
  const result: Record<string, any> = {}
  const today = dateString(new Date())
  for (const field of REPORT_FILTER_SCHEMAS[type].fields) {
    let value = data?.[field.key]
    // Saved stock filters used one day before the report gained a date range.
    if (
      type === 'ultimo_preco_estoque' &&
      field.key === 'dtend' &&
      value == null &&
      data?.dtstart !== undefined
    )
      value = data.dtstart
    if (value === undefined || value === null)
      value = clear ? '' : field.initial ?? (field.kind === 'date' ? today : '')
    if (field.kind === 'single') result[field.key] = singleSelection(field, value)
    else if (field.kind === 'multi') {
      result[field.key] = selectionValues(value)
      if (field.key === 'month')
        result[field.key] = result[field.key].map(
          (item: string) => field.options?.find((option) => option.label === item)?.value || item
        )
    } else result[field.key] = String(value).slice(0, field.kind === 'date' ? 10 : undefined)
  }
  return result
}
export function reportFilterPayload(
  type: ReportType,
  draft: Record<string, any>,
  options: Record<string, FilterOption[]> = {}
): any {
  const payload: Record<string, any> = {}
  for (const field of REPORT_FILTER_SCHEMAS[type].fields) {
    const value = draft[field.key]
    if (field.when && !field.when(draft)) {
      payload[field.key] = field.kind === 'multi' ? [] : ''
      continue
    }
    if (field.kind === 'single') payload[field.key] = singleSelection(field, value)
    else if (field.kind === 'multi') {
      const values = selectionValues(value)
      payload[field.key] =
        field.wire === 'flag'
          ? values.length === 1
            ? values[0]
            : ''
          : field.wire === 'options'
          ? values.map(
              (item) =>
                options[field.source || '']?.find((option) => option.value === item) || {
                  value: item,
                  label: item,
                }
            )
          : values
    } else payload[field.key] = value || ''
  }
  if (type === 'ultimo_preco_estoque')
    payload.status = payload.filterByDate === 'SIM' ? 'preco' : ''
  if (payload.cnpj && !isRelationshipReport(type))
    payload.cnpj = payload.cnpj
      .split(';')
      .map((item: string) => item.toUpperCase().replace(/[^A-Z0-9]/g, ''))
      .filter(Boolean)
      .join(';')
  if (payload.cpfVendedor)
    payload.cpfVendedor = payload.cpfVendedor
      .split(';')
      .map((item: string) => item.replace(/\D/g, ''))
      .filter(Boolean)
      .join(';')
  if (REPORT_FILTER_SCHEMAS[type].monthly) {
    const start = localDate(payload.dtstart),
      end = localDate(payload.dtend)
    if (start) payload.dtstart = dateString(new Date(start.getFullYear(), start.getMonth(), 1))
    if (end) payload.dtend = dateString(new Date(end.getFullYear(), end.getMonth() + 1, 0))
  }
  return isRelationshipReport(type) ? restoreLojasFilters(payload) : payload
}
export function validateReportFilters(type: ReportType, draft: Record<string, any>): string | null {
  const payload = reportFilterPayload(type, draft)
  if (isRelationshipReport(type)) return validateLojasFilters(payload)
  if (
    (payload.dtstart && !localDate(payload.dtstart)) ||
    (payload.dtend && !localDate(payload.dtend))
  )
    return 'Informe datas válidas.'
  if (payload.dtstart && payload.dtend && payload.dtstart > payload.dtend)
    return 'A data inicial não pode ser maior que a data final.'
  if (payload.idZona && !/^\d{1,19}$/.test(String(payload.idZona).trim()))
    return 'IDZona deve conter apenas números.'
  return null
}
